import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';
import { generateProductCostLedgerPdf } from './productCostLedgerPdf';

const ProductCostLedgerModal = ({ onClose }) => {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productId, setProductId] = useState('');
  const [fromDate, setFromDate] = useState('2026-01-01');
  const [toDate, setToDate] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingProducts(true);
        const res = await chemicalService.getProducts({ page: 1, limit: 1000 });
        setProducts(res.data || []);
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, []);

  const handleGenerate = async () => {
    if (!productId) return alert('❌ Select a product.');
    try {
      setGenerating(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await chemicalService.getProductCostLedger(productId, params);
      generateProductCostLedgerPdf(res.data, { from_date: fromDate, to_date: toDate });
      onClose();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || err.message || 'Failed to generate report'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.title}>📊 Product Cost Ledger</h3>
        <p style={styles.sub}>Sales · Commission · Expenses · Net Margin — per product</p>

        <div style={styles.field}>
          <label style={styles.label}>Product *</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            style={styles.input}
            disabled={loadingProducts}
          >
            <option value="">{loadingProducts ? 'Loading...' : '-- Select product --'}</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              style={styles.input}
            />
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              style={styles.input}
            />
          </div>
        </div>
        <p style={styles.hint}>Leave To Date blank to include everything up to today.</p>

        <div style={styles.actions}>
          <button onClick={onClose} style={styles.cancelButton} disabled={generating}>Cancel</button>
          <button onClick={handleGenerate} style={styles.generateButton} disabled={generating}>
            {generating ? '⏳ Generating...' : '🖨️ Generate PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '440px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' },
  title: { margin: '0 0 4px', fontSize: '20px', color: '#2c3e50' },
  sub: { margin: '0 0 20px', fontSize: '13px', color: '#95a5a6' },
  field: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', color: '#2c3e50', background: 'white', cursor: 'pointer', outline: 'none' },
  hint: { fontSize: '12px', color: '#95a5a6', margin: '2px 0 20px' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  cancelButton: { padding: '10px 18px', background: 'white', color: '#555', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  generateButton: { padding: '10px 18px', background: '#9b59b6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' },
};

export default ProductCostLedgerModal;